/**
 * Provisioning runner store — fetch a diagnosis for one workflow against
 * one network, mutate which settings will be pushed, then kick off the
 * actual provision via celery.
 *
 * Diagnoses are cached by `workflowSlug:networkId` so navigating back to a
 * runner returns instantly.
 */
import { create } from "zustand";
import { get as apiGet, post as apiPost, ApiError } from "@/api/client";
import { pollCeleryTask } from "@/api/celery";
import {
  ProvisioningDiagnosisSchema,
  type ProvisioningDiagnosis,
} from "@/api/schemas/provisioning";
import { CeleryTaskSchema, type CeleryProgress } from "@/api/schemas/meraki";
import { MOCK_DIAGNOSES, buildPlaceholderDiagnosis } from "@/api/fixtures/provisioning";

/** ProvisioningDiagnosis with an internal freshness timestamp. */
interface CachedDiagnosis extends ProvisioningDiagnosis {
  /** Internal — timestamp of the last successful fetch. Drives the
   *  stale-while-revalidate window in `load()`. */
  _fetchedAt?: number;
}

/** How long a diagnosis result is considered fresh enough to skip re-fetching. */
const FRESHNESS_MS = 30_000;

interface State {
  byKey: Record<string, CachedDiagnosis>;
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;

  /**
   * Fetch (or return cached) diagnosis. Pass `{ force: true }` to bypass
   * the freshness window — typically wired to an explicit "Re-diagnose"
   * button so background loads can't hammer the underlying Meraki API.
   */
  load: (workflowSlug: string, networkId: string, opts?: { force?: boolean }) => Promise<void>;
  /** Toggle whether a single setting will be included in the next push. */
  toggleWillPush: (workflowSlug: string, networkId: string, settingId: string) => void;
  /** Set every drift/missing/error setting to push (or none). */
  setAllWillPush: (workflowSlug: string, networkId: string, value: boolean) => void;
  /** POST the provision and stream celery progress through `onProgress`. */
  provision: (
    workflowSlug: string,
    networkId: string,
    onProgress: (p: CeleryProgress) => void,
  ) => Promise<CeleryProgress>;
}

const keyOf = (slug: string, net: string) => `${slug}:${net}`;

export const useProvisioningRun = create<State>((set, get) => ({
  byKey: {},
  status: "idle",
  error: null,

  load: async (workflowSlug, networkId, opts) => {
    if (get().status === "loading") return;
    const k = keyOf(workflowSlug, networkId);
    // Stale-while-revalidate: skip the network call if the cached entry
    // is still fresh and the caller hasn't asked to force-refresh.
    const cached = get().byKey[k];
    if (!opts?.force && cached?._fetchedAt && Date.now() - cached._fetchedAt < FRESHNESS_MS) {
      return;
    }
    set({ status: "loading", error: null });
    try {
      const raw = await apiGet<unknown>(`/meraki/workflows/${workflowSlug}/diagnose/`, {
        network_id: networkId,
      });
      const parsed = ProvisioningDiagnosisSchema.parse(raw);
      set((s) => ({
        byKey: { ...s.byKey, [k]: { ...parsed, _fetchedAt: Date.now() } },
        status: "ready",
      }));
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      // Synthesize a fallback diagnosis. If we have a fixture for the slug,
      // use it; otherwise return an empty placeholder so the page renders.
      const fallback = MOCK_DIAGNOSES[workflowSlug] ?? buildPlaceholderDiagnosis(workflowSlug);
      set((s) => ({
        byKey: { ...s.byKey, [k]: { ...fallback, networkId, _fetchedAt: Date.now() } },
        status: "error",
        error: `${message} — showing fixture data`,
      }));
    }
  },

  toggleWillPush: (workflowSlug, networkId, settingId) => {
    set((s) => {
      const k = keyOf(workflowSlug, networkId);
      const d = s.byKey[k];
      if (!d) return s;
      return {
        byKey: {
          ...s.byKey,
          [k]: {
            ...d,
            settings: d.settings.map((x) =>
              x.id === settingId ? { ...x, willPush: !x.willPush } : x,
            ),
          },
        },
      };
    });
  },

  setAllWillPush: (workflowSlug, networkId, value) => {
    set((s) => {
      const k = keyOf(workflowSlug, networkId);
      const d = s.byKey[k];
      if (!d) return s;
      return {
        byKey: {
          ...s.byKey,
          [k]: {
            ...d,
            settings: d.settings.map((x) =>
              // Compliant + skipped settings are never pushed; only flip the
              // ones that have something to do.
              x.state === "compliant" || x.state === "skipped"
                ? x
                : { ...x, willPush: value },
            ),
          },
        },
      };
    });
  },

  provision: async (workflowSlug, networkId, onProgress) => {
    const k = keyOf(workflowSlug, networkId);
    const d = get().byKey[k];
    if (!d) throw new Error("provision: no diagnosis loaded");
    const settings = d.settings.flatMap((s) => (s.willPush ? [s.id] : []));
    const raw = await apiPost<unknown>(`/meraki/workflows/${workflowSlug}/provision/`, {
      network_id: networkId,
      setting_ids: settings,
    });
    const parsed = CeleryTaskSchema.safeParse(raw);
    if (!parsed.success) {
      // No task id returned — pretend immediate success so the UI bounces forward.
      onProgress({ state: "SUCCESS", message: "no task id returned by server" });
      return { state: "SUCCESS", message: "no task id returned by server" };
    }
    return await pollCeleryTask(parsed.data.taskId, onProgress);
  },
}));
